<script setup>
import { ref, computed } from 'vue';
import { useRouter } from 'vue-router';
import { useToast } from 'primevue/usetoast';
import { useAuthStore } from '@/store/AuthStore';
import { useFetch } from '@/plugins/fetch';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Checkbox from 'primevue/checkbox';
import Button from 'primevue/button';

const router = useRouter();
const toast = useToast();
const authStore = useAuthStore();
const fetch = useFetch();

const user = computed(() => authStore.getUser);

// Dialog states
const showDeleteDialog = ref(false);
const showConfirmDialog = ref(false);
const showExportDialog = ref(false);

// Form states
const confirmEmail = ref('');
const confirmText = ref('');
const password = ref('');
const acknowledgements = ref({
  dataLoss: false,
  noRecovery: false,
  subscriptionCancel: false,
});
const deleteReason = ref('');

// Loading states
const isDeleting = ref(false);
const isExporting = ref(false);
const exportProgress = ref(0);

// Validation
const isEmailMatch = computed(() => confirmEmail.value === user.value?.email);
const isConfirmTextMatch = computed(() => confirmText.value === 'DELETE MY ACCOUNT');
const allAcknowledged = computed(() => 
  acknowledgements.value.dataLoss && 
  acknowledgements.value.noRecovery && 
  acknowledgements.value.subscriptionCancel
);
const canProceed = computed(() => isEmailMatch.value && allAcknowledged.value);
const canDelete = computed(() => isConfirmTextMatch.value && password.value.length > 0);

// Methods
const openDeleteDialog = () => {
  confirmEmail.value = '';
  acknowledgements.value = { dataLoss: false, noRecovery: false, subscriptionCancel: false };
  deleteReason.value = '';
  showDeleteDialog.value = true;
};

const proceedToConfirm = () => {
  showDeleteDialog.value = false;
  confirmText.value = '';
  password.value = '';
  showConfirmDialog.value = true;
};

const cancelDeletion = () => {
  showDeleteDialog.value = false;
  showConfirmDialog.value = false;
  confirmEmail.value = '';
  confirmText.value = '';
  password.value = '';
  acknowledgements.value = { dataLoss: false, noRecovery: false, subscriptionCancel: false };
};

const deleteAccount = async () => {
  if (!canDelete.value) return;
  
  isDeleting.value = true;
  try {
    await fetch('users/me/delete/', {
      method: 'POST',
      body: {
        password: password.value,
        reason: deleteReason.value,
        confirmation: confirmText.value,
      },
    });
    
    showConfirmDialog.value = false;
    toast.add({
      severity: 'info',
      summary: 'Account Scheduled for Deletion',
      detail: 'Your account will be deleted within 30 days. Check your email for confirmation.',
      life: 10000,
    });
    
    // Log out after a delay
    setTimeout(async () => {
      await authStore.logout();
      router.push('/signin');
    }, 3000);
    
  } catch (error) {
    const message = error?.response?.data?.message || 'Failed to delete account. Please check your password.';
    toast.add({
      severity: 'error',
      summary: 'Deletion Failed',
      detail: message,
      life: 5000,
    });
  } finally {
    isDeleting.value = false;
  }
};

const exportData = async () => {
  isExporting.value = true;
  exportProgress.value = 0;
  
  try {
    // Simulate progress for UX
    const progressInterval = setInterval(() => {
      if (exportProgress.value < 90) {
        exportProgress.value += 10;
      }
    }, 200);
    
    const response = await fetch('users/me/export/', {
      method: 'POST',
      responseType: 'blob',
    });
    
    clearInterval(progressInterval);
    exportProgress.value = 100;
    
    // Handle the download
    if (response instanceof Blob) {
      const url = window.URL.createObjectURL(response);
      const a = document.createElement('a');
      a.href = url;
      a.download = `calid-data-export-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } else if (response?.download_url) {
      window.open(response.download_url, '_blank');
    }
    
    toast.add({
      severity: 'success',
      summary: 'Export Complete',
      detail: 'Your data has been exported successfully.',
      life: 5000,
    });
    
    showExportDialog.value = false;
  } catch {
    toast.add({
      severity: 'error',
      summary: 'Export Failed',
      detail: 'Failed to export your data. Please try again later.',
      life: 5000,
    });
  } finally {
    isExporting.value = false;
    exportProgress.value = 0;
  }
};

const deleteReasons = [
  'No longer need the service',
  'Found a better alternative',
  'Too expensive',
  'Privacy concerns',
  'Technical issues',
  'Other',
];
</script>

<template>
  <div class="account-deletion-card">
    <div class="card-header">
      <div class="header-icon danger">
        <i class="fa fa-exclamation-triangle"></i>
      </div>
      <div class="header-text">
        <h3>Danger Zone</h3>
        <p>Irreversible account actions</p>
      </div>
    </div>

    <div class="card-body">
      <!-- Data Export Section -->
      <div class="action-section">
        <div class="action-info">
          <div class="action-icon">
            <i class="fa fa-download"></i>
          </div>
          <div class="action-details">
            <h4>Export Your Data</h4>
            <p>Download a copy of all your personal data, sensors, and readings in JSON format. This is your right under GDPR.</p>
          </div>
        </div>
        <button class="action-btn export" @click="showExportDialog = true">
          <i class="fa fa-cloud-download"></i>
          Export Data
        </button>
      </div>

      <div class="divider"></div>

      <!-- Account Deletion Section -->
      <div class="action-section danger">
        <div class="action-info">
          <div class="action-icon danger">
            <i class="fa fa-trash"></i>
          </div>
          <div class="action-details">
            <h4>Delete Account</h4>
            <p>Permanently delete your account and all associated data. This action cannot be undone.</p>
            <ul class="consequences">
              <li><i class="fa fa-times"></i> All sensors and data will be deleted</li>
              <li><i class="fa fa-times"></i> Active subscriptions will be cancelled</li>
              <li><i class="fa fa-times"></i> You will lose access immediately</li>
            </ul>
          </div>
        </div>
        <button class="action-btn danger" @click="openDeleteDialog">
          <i class="fa fa-trash"></i>
          Delete Account
        </button>
      </div>
    </div>

    <!-- Export Dialog -->
    <Dialog
      v-model:visible="showExportDialog"
      :style="{ width: '450px' }"
      modal
      class="gdpr-dialog"
    >
      <template #header>
        <div class="dialog-header">
          <div class="dialog-icon export">
            <i class="fa fa-download"></i>
          </div>
          <div>
            <h3>Export Your Data</h3>
            <p>GDPR Data Portability Request</p>
          </div>
        </div>
      </template>

      <div class="export-content">
        <p>Your export will include:</p>
        <ul class="export-includes">
          <li><i class="fa fa-check"></i> Profile information</li>
          <li><i class="fa fa-check"></i> All sensor configurations</li>
          <li><i class="fa fa-check"></i> Historical sensor readings</li>
          <li><i class="fa fa-check"></i> Alert configurations</li>
          <li><i class="fa fa-check"></i> Account settings</li>
        </ul>

        <div v-if="isExporting" class="export-progress">
          <div class="progress-bar">
            <div class="progress-fill" :style="{ width: `${exportProgress}%` }"></div>
          </div>
          <span class="progress-text">{{ exportProgress }}% Complete</span>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <Button 
            label="Cancel" 
            class="cancel-btn" 
            @click="showExportDialog = false"
            :disabled="isExporting"
          />
          <Button 
            label="Download Export" 
            icon="fa fa-download"
            class="export-btn"
            @click="exportData"
            :loading="isExporting"
          />
        </div>
      </template>
    </Dialog>

    <!-- Delete Dialog - Step 1 -->
    <Dialog
      v-model:visible="showDeleteDialog"
      :style="{ width: '520px' }"
      modal
      :closable="!isDeleting"
      class="gdpr-dialog danger"
    >
      <template #header>
        <div class="dialog-header">
          <div class="dialog-icon danger">
            <i class="fa fa-exclamation-triangle"></i>
          </div>
          <div>
            <h3>Delete Your Account</h3>
            <p>This action is permanent and irreversible</p>
          </div>
        </div>
      </template>

      <div class="delete-content">
        <div class="warning-banner">
          <i class="fa fa-info-circle"></i>
          <span>Before proceeding, we recommend exporting your data.</span>
        </div>

        <!-- Email Confirmation -->
        <div class="form-field">
          <label>Confirm your email address</label>
          <InputText 
            v-model="confirmEmail" 
            :placeholder="user?.email"
            :class="{ 'valid': isEmailMatch && confirmEmail }"
          />
          <small v-if="confirmEmail && !isEmailMatch" class="error-text">
            Email does not match your account email
          </small>
        </div>

        <!-- Reason -->
        <div class="form-field">
          <label>Why are you leaving? (Optional)</label>
          <select v-model="deleteReason" class="reason-select">
            <option value="">Select a reason...</option>
            <option v-for="reason in deleteReasons" :key="reason" :value="reason">
              {{ reason }}
            </option>
          </select>
        </div>

        <!-- Acknowledgements -->
        <div class="acknowledgements">
          <label class="section-label">Please acknowledge the following:</label>
          
          <div class="ack-item">
            <Checkbox v-model="acknowledgements.dataLoss" :binary="true" inputId="ack-data" />
            <label for="ack-data">I understand all my data will be permanently deleted</label>
          </div>
          
          <div class="ack-item">
            <Checkbox v-model="acknowledgements.noRecovery" :binary="true" inputId="ack-recovery" />
            <label for="ack-recovery">I understand this action cannot be undone</label>
          </div>
          
          <div class="ack-item">
            <Checkbox v-model="acknowledgements.subscriptionCancel" :binary="true" inputId="ack-sub" />
            <label for="ack-sub">I understand my subscription will be cancelled without refund</label>
          </div>
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <Button label="Cancel" class="cancel-btn" @click="cancelDeletion" />
          <Button 
            label="Continue" 
            icon="fa fa-arrow-right"
            iconPos="right"
            class="danger-btn"
            @click="proceedToConfirm"
            :disabled="!canProceed"
          />
        </div>
      </template>
    </Dialog>

    <!-- Delete Dialog - Step 2 (Final Confirmation) -->
    <Dialog
      v-model:visible="showConfirmDialog"
      :style="{ width: '480px' }"
      modal
      :closable="!isDeleting"
      class="gdpr-dialog danger"
    >
      <template #header>
        <div class="dialog-header">
          <div class="dialog-icon danger pulse">
            <i class="fa fa-exclamation-triangle"></i>
          </div>
          <div>
            <h3>Final Confirmation</h3>
            <p>Point of no return</p>
          </div>
        </div>
      </template>

      <div class="confirm-content">
        <div class="final-warning">
          <p>To confirm deletion, please type:</p>
          <code>DELETE MY ACCOUNT</code>
        </div>

        <div class="form-field">
          <InputText 
            v-model="confirmText" 
            placeholder="Type the text above"
            :class="{ 'valid': isConfirmTextMatch }"
          />
        </div>

        <div class="form-field">
          <label>Enter your password</label>
          <InputText 
            v-model="password" 
            type="password"
            placeholder="Your current password"
          />
        </div>
      </div>

      <template #footer>
        <div class="dialog-footer">
          <Button 
            label="Go Back" 
            icon="fa fai-arrow-left"
            class="cancel-btn" 
            @click="showConfirmDialog = false; showDeleteDialog = true"
            :disabled="isDeleting"
          />
          <Button 
            label="Delete My Account" 
            icon="fa fa-trash"
            class="delete-btn"
            @click="deleteAccount"
            :disabled="!canDelete"
            :loading="isDeleting"
          />
        </div>
      </template>
    </Dialog>
  </div>
</template>

<style scoped>
.account-deletion-card {
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 16px;
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  padding: 1.25rem;
  background: rgba(239, 68, 68, 0.05);
  border-bottom: 1px solid rgba(239, 68, 68, 0.1);
}

.header-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.header-icon.danger {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.header-text h3 {
  font-size: 1.1rem;
  font-weight: 600;
  color: #ef4444;
  margin: 0;
}

.header-text p {
  font-size: 0.85rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
}

.card-body {
  padding: 1.25rem;
}

.action-section {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1.5rem;
  flex-wrap: wrap;
}

.action-info {
  display: flex;
  gap: 1rem;
  flex: 1;
}

.action-icon {
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: rgba(0, 212, 255, 0.1);
  display: flex;
  align-items: center;
  justify-content: center;
  color: #00d4ff;
  flex-shrink: 0;
}

.action-icon.danger {
  background: rgba(239, 68, 68, 0.1);
  color: #ef4444;
}

.action-details h4 {
  font-size: 1rem;
  font-weight: 600;
  color: var(--calid-text-primary, #fff);
  margin: 0 0 0.25rem;
}

.action-details p {
  font-size: 0.85rem;
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
  line-height: 1.5;
}

.consequences {
  list-style: none;
  padding: 0;
  margin: 0.75rem 0 0;
}

.consequences li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.8rem;
  color: #f87171;
  margin-bottom: 0.25rem;
}

.consequences li i {
  font-size: 0.7rem;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1.25rem;
  border-radius: 10px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: 1px solid;
  white-space: nowrap;
}

.action-btn.export {
  background: rgba(0, 212, 255, 0.1);
  border-color: rgba(0, 212, 255, 0.3);
  color: #00d4ff;
}

.action-btn.export:hover {
  background: rgba(0, 212, 255, 0.2);
  transform: translateY(-1px);
}

.action-btn.danger {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.3);
  color: #ef4444;
}

.action-btn.danger:hover {
  background: rgba(239, 68, 68, 0.2);
  transform: translateY(-1px);
}

.divider {
  height: 1px;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.1), transparent);
  margin: 1.5rem 0;
}

/* Dialog Styles */
:deep(.gdpr-dialog .p-dialog) {
  background: var(--calid-bg-secondary, #111827);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 20px;
}

:deep(.gdpr-dialog.danger .p-dialog) {
  border-color: rgba(239, 68, 68, 0.2);
}

:deep(.gdpr-dialog .p-dialog-header) {
  background: transparent;
  border: none;
  padding: 1.5rem 1.5rem 0;
}

:deep(.gdpr-dialog .p-dialog-content) {
  background: transparent;
  padding: 1.5rem;
}

:deep(.gdpr-dialog .p-dialog-footer) {
  background: transparent;
  border: none;
  padding: 0 1.5rem 1.5rem;
}

.dialog-header {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.dialog-icon {
  width: 48px;
  height: 48px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
}

.dialog-icon.export {
  background: rgba(0, 212, 255, 0.15);
  color: #00d4ff;
}

.dialog-icon.danger {
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
}

.dialog-icon.pulse {
  animation: dangerPulse 1.5s ease-in-out infinite;
}

@keyframes dangerPulse {
  0%, 100% { box-shadow: 0 0 0 0 rgba(239, 68, 68, 0.4); }
  50% { box-shadow: 0 0 0 10px rgba(239, 68, 68, 0); }
}

.dialog-header h3 {
  font-size: 1.25rem;
  color: var(--calid-text-primary, #fff);
  margin: 0;
}

.dialog-header p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0;
  font-size: 0.85rem;
}

/* Export Dialog */
.export-content p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0 0 1rem;
}

.export-includes {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem;
}

.export-includes li {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  color: var(--calid-text-primary, #fff);
  font-size: 0.9rem;
}

.export-includes li i {
  color: #10b981;
}

.export-progress {
  margin-top: 1rem;
}

.progress-bar {
  height: 8px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
}

.progress-fill {
  height: 100%;
  background: linear-gradient(90deg, #00d4ff, #7c3aed);
  border-radius: 4px;
  transition: width 0.3s ease;
}

.progress-text {
  display: block;
  text-align: center;
  margin-top: 0.5rem;
  font-size: 0.85rem;
  color: var(--calid-text-secondary, #94a3b8);
}

/* Delete Dialog */
.delete-content,
.confirm-content {
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.warning-banner {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.875rem 1rem;
  background: rgba(245, 158, 11, 0.1);
  border: 1px solid rgba(245, 158, 11, 0.3);
  border-radius: 10px;
  color: #fbbf24;
  font-size: 0.85rem;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-field label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--calid-text-secondary, #94a3b8);
}

:deep(.form-field .p-inputtext) {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  color: var(--calid-text-primary, #fff);
}

:deep(.form-field .p-inputtext:focus) {
  border-color: rgba(239, 68, 68, 0.5);
  box-shadow: 0 0 0 2px rgba(239, 68, 68, 0.1);
}

:deep(.form-field .p-inputtext.valid) {
  border-color: rgba(16, 185, 129, 0.5);
}

.error-text {
  color: #f87171;
  font-size: 0.75rem;
}

.reason-select {
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  padding: 0.75rem 1rem;
  color: var(--calid-text-primary, #fff);
  font-size: 0.9rem;
  cursor: pointer;
}

.reason-select option {
  background: #1a1a2e;
  color: #fff;
}

.acknowledgements {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.section-label {
  font-size: 0.85rem;
  font-weight: 500;
  color: var(--calid-text-secondary, #94a3b8);
  margin-bottom: 0.25rem;
}

.ack-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.ack-item label {
  font-size: 0.85rem;
  color: var(--calid-text-primary, #fff);
  cursor: pointer;
}

:deep(.ack-item .p-checkbox .p-checkbox-box) {
  background: rgba(255, 255, 255, 0.05);
  border-color: rgba(255, 255, 255, 0.2);
}

:deep(.ack-item .p-checkbox .p-checkbox-box.p-highlight) {
  background: #ef4444;
  border-color: #ef4444;
}

/* Final Confirmation */
.final-warning {
  text-align: center;
  padding: 1.5rem;
  background: rgba(239, 68, 68, 0.05);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 12px;
}

.final-warning p {
  color: var(--calid-text-secondary, #94a3b8);
  margin: 0 0 0.75rem;
}

.final-warning code {
  display: inline-block;
  padding: 0.5rem 1rem;
  background: rgba(239, 68, 68, 0.1);
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  color: #ef4444;
  font-family: 'JetBrains Mono', monospace;
  font-size: 0.9rem;
  font-weight: 600;
}

/* Dialog Footer */
.dialog-footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
}

:deep(.cancel-btn) {
  padding: 0.75rem 1.25rem;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: var(--calid-text-secondary, #94a3b8);
}

:deep(.cancel-btn:hover) {
  background: rgba(255, 255, 255, 0.05);
}

:deep(.export-btn) {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #00d4ff 0%, #7c3aed 100%);
  border: none;
  border-radius: 10px;
  color: white;
}

:deep(.danger-btn) {
  padding: 0.75rem 1.25rem;
  background: rgba(239, 68, 68, 0.2);
  border: 1px solid rgba(239, 68, 68, 0.4);
  border-radius: 10px;
  color: #ef4444;
}

:deep(.danger-btn:hover:not(:disabled)) {
  background: rgba(239, 68, 68, 0.3);
}

:deep(.delete-btn) {
  padding: 0.75rem 1.25rem;
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
  border: none;
  border-radius: 10px;
  color: white;
}

:deep(.delete-btn:disabled) {
  opacity: 0.5;
}

/* Responsive */
@media (max-width: 640px) {
  .action-section {
    flex-direction: column;
  }

  .action-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
